'use client'

import { cn } from '@/lib/utils'

type MentionChipProps = {
  display: string
  kind?: string
  /** When true, the chip renders with strikethrough (deleted resource). */
  stale?: boolean
  /** Callback for the remove button (input overlay mode). */
  onRemove?: () => void
  /** Click handler (message mode — navigates to resource). */
  onClick?: () => void
  /** Extra class names. */
  className?: string
}

// Stephen 2026-05-12: "the badge should have no padding, border, or
// background. just bold, atrium-600 text." Both input mode and message
// mode now render as minimal inline text — bold accent-600 (atrium-600
// in his vocab) with no chrome. Input mode keeps a tiny ✕ to drop the
// mention; message mode is a clickable inline button that routes to
// the resource. Stale (deleted) mentions strike through.
//
// We deliberately drop the @ glyph too — Stephen's request was "just
// the badge" and the mention is already preceded by the user's typed
// "@" in the textarea / message text. Showing two @'s would look weird.

export function MentionChip({ display, kind: _kind, stale, onRemove, onClick, className }: MentionChipProps) {
  const isInputMode = Boolean(onRemove)

  if (isInputMode) {
    return (
      <span
        className={cn(
          'inline-flex items-center align-baseline font-bold text-accent-600 select-none',
          stale && 'line-through opacity-50',
          className
        )}
      >
        @<span className="ml-0.5">{display}</span>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove?.() }}
          aria-label={`Remove ${display}`}
          className="ml-0.5 text-accent-600/60 hover:text-accent-700 transition-colors leading-none"
        >
          ×
        </button>
      </span>
    )
  }

  // Message mode — clickable inline element that routes to the resource.
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={stale}
      aria-label={stale ? `${display} (deleted)` : `Open ${display}`}
      className={cn(
        'inline align-baseline font-bold text-accent-600 transition-colors',
        stale
          ? 'cursor-default line-through opacity-50'
          : 'cursor-pointer hover:text-accent-700 hover:underline',
        className
      )}
    >
      @{display}
    </button>
  )
}
